// Fase B.5c.3c.3b -- funcoes PURAS (sem React, sem HTTP) que decidem a
// apresentacao das acoes financeiras da tela "Cobrancas de Eventos".
//
// PRINCIPIO CENTRAL: o React NUNCA reconstroi regra financeira. A UNICA
// fonte semantica para a acao primaria e' `item.acao_financeira`
// (calculado pelo backend, Fase B.5c.3c.3a) -- nunca status_emissao/
// status_cobranca/status_participacao/status_inscricao/provider/
// data_vencimento/linha_digitavel. Esses campos continuam existindo
// para EXIBICAO (Tags informativos), nunca para decidir Emitir/
// Verificar.
//
// Extraidas em modulo separado (em vez de inline no componente) para
// serem testaveis via Jest puro -- o projeto nao tem infraestrutura de
// teste de componente React (sem @testing-library/react instalado,
// unico precedente de teste frontend e' src/utils/apiError.test.js,
// que testa funcao pura). Nenhuma dependencia nova foi introduzida.

export const ACAO_FINANCEIRA = Object.freeze({
  GERAR_COBRANCA: 'GERAR_COBRANCA',
  EMITIR_BOLETO: 'EMITIR_BOLETO',
  VERIFICAR_EMISSAO: 'VERIFICAR_EMISSAO',
  // Fase cancelamento-canonico (frontend) -- mesmo principio de
  // "dica/capacidade de UI, nunca autorizacao" ja documentado acima.
  // Igualdade estrita contra `item.acao_financeira`, nunca inferencia
  // por status_cobranca/status_emissao/provider/linha_digitavel.
  CANCELAR_COBRANCA: 'CANCELAR_COBRANCA',
  VERIFICAR_CANCELAMENTO: 'VERIFICAR_CANCELAMENTO',
  NENHUMA: 'NENHUMA',
});

/**
 * Dado o array de itens da listagem (uma linha = uma parcela), retorna
 * um mapa { evento_inscricao_id: evento_inscricao_parcela_id } contendo
 * SOMENTE a primeira linha elegivel de cada inscricao para "Gerar
 * cobranca" -- o endpoint e' por INSCRICAO (gera todas as parcelas
 * pendentes de uma vez), mas a tabela e' por PARCELA, entao mostrar o
 * botao em toda linha candidata duplicaria a acao visualmente.
 *
 * Fonte semantica EXCLUSIVA: `item.acao_financeira === GERAR_COBRANCA`
 * (nunca status_inscricao/status_participacao/status_cobranca).
 */
export function primeiraLinhaParaGerarCobrancaPorInscricao(itens) {
  const vistos = new Set();
  const mapa = {};
  (itens || []).forEach((item) => {
    if (item.acao_financeira !== ACAO_FINANCEIRA.GERAR_COBRANCA) {
      return;
    }
    if (!vistos.has(item.evento_inscricao_id)) {
      vistos.add(item.evento_inscricao_id);
      mapa[item.evento_inscricao_id] = item.evento_inscricao_parcela_id;
    }
  });
  return mapa;
}

/**
 * Fase Cobrancas-Operacional Fase 1 (Secao 10/12 da instrucao) -- decide
 * se o botao "Cancelar cobrança" deve aparecer, cobrindo os DOIS
 * caminhos possiveis (mutuamente independentes no backend, mas que
 * levam ao MESMO endpoint/modal/handler no frontend -- Secao 11):
 *   - `acao_financeira === CANCELAR_COBRANCA` (cancelamento externo ou
 *     local, ja era a acao primaria); OU
 *   - `item.pode_cancelar_localmente === true` (capacidade ADITIVA,
 *     pode coexistir com acao_financeira === EMITIR_BOLETO).
 * Igualdade estrita em ambos -- nunca inferido por status_cobranca/
 * status_emissao/provider/linha_digitavel/financial_charge_id isolado.
 * Exatamente UM botao e' mostrado mesmo quando as duas condicoes sao
 * verdadeiras simultaneamente (single boolean, nunca dois `if`
 * independentes no componente).
 */
export function mostrarBotaoCancelarCobranca(item) {
  if (!item || !item.financial_charge_id) {
    return false;
  }
  return item.acao_financeira === ACAO_FINANCEIRA.CANCELAR_COBRANCA || item.pode_cancelar_localmente === true;
}

/**
 * Mapeia `status_emissao` retornado pelo POST .../emitir-boleto para o
 * feedback a exibir ao admin. `nivel` corresponde diretamente aos
 * metodos do antd `message` (success/warning/error/info).
 */
export function mensagemFeedbackEmissao(statusEmissao) {
  switch (statusEmissao) {
    case 'EMITIDO':
      return { nivel: 'success', texto: 'Boleto emitido com sucesso.' };
    case 'FALHA_TECNICA':
      return { nivel: 'error', texto: 'Não foi possível concluir a emissão por uma falha técnica.' };
    case 'FALHA_PROVIDER':
      return { nivel: 'error', texto: 'O provedor financeiro recusou a emissão.' };
    case 'VERIFICACAO_NECESSARIA':
      return {
        nivel: 'warning',
        texto: 'Não foi possível confirmar o resultado da emissão. Verifique a situação antes de tentar novamente.',
      };
    default:
      return { nivel: 'info', texto: 'Emissão processada.' };
  }
}

/**
 * Mapeia `status_emissao` retornado pelo POST .../verificar-emissao
 * para o feedback a exibir ao admin.
 */
export function mensagemFeedbackVerificacao(statusEmissao) {
  switch (statusEmissao) {
    case 'EMITIDO':
      return { nivel: 'success', texto: 'Emissão confirmada.' };
    case 'VERIFICACAO_NECESSARIA':
      return {
        nivel: 'warning',
        texto: 'A emissão ainda não pôde ser confirmada. Tente verificar novamente mais tarde.',
      };
    default:
      return { nivel: 'info', texto: 'Situação verificada.' };
  }
}

/**
 * Mapeia o HTTP status de uma falha de emitir-boleto/verificar-emissao/
 * cancelar/verificar-cancelamento para uma mensagem amigavel + se a
 * listagem deve ser recarregada automaticamente. Os endpoints sao
 * autoritativos -- o estado pode ter mudado entre o GET da listagem e o
 * clique do admin, entao 404/409 sao tratados como "situacao mudou",
 * nunca como bug. `mensagem422` e' opcional (default preserva 100% o
 * texto ja usado por emitir-boleto) -- 422 do backend e' erro de
 * VALIDACAO (ex.: `motivo_cancelamento` invalido no cancelamento), nunca
 * deve ser escondido nem generico demais para cada acao.
 */
export function mensagemErroAcaoFinanceira(
  status,
  fallback,
  mensagem422 = 'Não foi possível emitir o boleto: esta cobrança não está elegível para emissão no momento.'
) {
  if (status === 404) {
    return {
      nivel: 'error',
      texto: 'Esta cobrança não está mais disponível. Atualizamos a lista para você.',
      deveRecarregar: true,
    };
  }
  if (status === 409) {
    return {
      nivel: 'warning',
      texto: 'A situação desta cobrança mudou. Atualizamos a lista para você.',
      deveRecarregar: true,
    };
  }
  if (status === 422) {
    return {
      nivel: 'error',
      texto: mensagem422,
      deveRecarregar: false,
    };
  }
  if (status === 503) {
    return {
      nivel: 'error',
      texto: 'O serviço financeiro está temporariamente indisponível. Tente novamente em instantes.',
      deveRecarregar: false,
    };
  }
  return { nivel: 'error', texto: fallback, deveRecarregar: false };
}

/**
 * Mapeia `status_operacao` (+ `status_charge` para o caso de
 * cancelamento LOCAL, onde `status_operacao` vem `null`) retornado por
 * POST .../cancelar e POST .../verificar-cancelamento para o feedback a
 * exibir ao admin. `contexto` distingue os dois textos (Secao 6 vs Secao
 * 9 da instrucao) -- nunca afirma sucesso quando o backend nao confirmou
 * inequivocamente (resposta desconhecida cai em warning, fail closed).
 */
export function mensagemFeedbackCancelamento(statusOperacao, { statusCharge, contexto = 'cancelar' } = {}) {
  const sucesso = statusOperacao === 'SUCESSO' || (statusOperacao == null && statusCharge === 'CANCELADA');
  if (sucesso) {
    return contexto === 'verificar'
      ? { nivel: 'success', texto: 'Cancelamento confirmado.' }
      : { nivel: 'success', texto: 'Cobrança cancelada com sucesso.' };
  }
  if (statusOperacao === 'RESULTADO_INCERTO') {
    return contexto === 'verificar'
      ? {
          nivel: 'warning',
          texto: 'O cancelamento ainda não pôde ser confirmado. Tente verificar novamente mais tarde.',
        }
      : {
          nivel: 'warning',
          texto: 'Não foi possível confirmar o cancelamento. Verifique a situação antes de tentar novamente.',
        };
  }
  if (statusOperacao === 'CONFLITO_PAGAMENTO') {
    return contexto === 'verificar'
      ? { nivel: 'error', texto: 'Foi detectada evidência de pagamento. A situação requer revisão manual.' }
      : {
          nivel: 'error',
          texto: 'Foi detectada evidência de pagamento para esta cobrança. O cancelamento foi bloqueado e requer revisão manual.',
        };
  }
  if (statusOperacao === 'ERRO_PROVIDER') {
    return { nivel: 'error', texto: 'O provedor financeiro recusou o cancelamento.' };
  }
  if (statusOperacao === 'ERRO_TECNICO') {
    return contexto === 'verificar'
      ? {
          nivel: 'warning',
          texto: 'Não foi possível concluir a verificação por uma falha técnica. A cobrança foi restaurada.',
        }
      : { nivel: 'warning', texto: 'Não foi possível concluir o cancelamento por uma falha técnica.' };
  }
  // Resposta desconhecida -- fail closed, NUNCA afirma sucesso.
  return {
    nivel: 'warning',
    texto: 'Não foi possível confirmar o resultado. Verifique a situação desta cobrança.',
  };
}

/** Chave composta (tipo da acao + id) usada para loading/disabled por
 * operacao/registro -- nunca trava a tabela inteira. */
export function chaveAcaoEmAndamento(tipo, id) {
  return `${tipo}:${id}`;
}

// ---------------------------------------------------------------------
// Fase 2B (frontend agrupado) -- helpers puros novos para a visao
// agrupada de "Cobrancas de Eventos" (GET /api/eventos/cobrancas/
// agrupado). `status_resumido` e' calculado e VALIDADO exclusivamente
// pelo backend (STATUS_RESUMIDO_GRUPO_VALIDOS em
// evento_cobranca_admin_service.py) -- os mapas abaixo sao SOMENTE
// apresentacao (label PT-BR + cor de Tag), nunca reconstroem a regra de
// precedencia entre os 10 codigos.
//
// Label/cor ficam aqui (nao inline no componente, diferente de
// STATUS_COBRANCA_COLORS/LABELS em CobrancasEventosAdmin.js) de proposito:
// e' a UNICA forma de cobri-los com teste automatizado dado que o
// projeto nao tem infraestrutura de teste de componente React (Secao
// 21 da instrucao exige teste para os 10 codigos -> label/cor).
const STATUS_RESUMIDO_LABELS = {
  REVISAO_NECESSARIA: 'Revisão necessária',
  VERIFICACAO_PENDENTE: 'Verificação pendente',
  CANCELAMENTO_EM_ANDAMENTO: 'Cancelamento em andamento',
  POSSUI_VENCIDA: 'Possui parcela vencida',
  // Fase Eventos-Recobranca 3.1 -- coerente com a tag individual
  // RECOBRANCA_NECESSARIA ("Recobrança necessária"), visivel com o grupo
  // colapsado.
  POSSUI_RECOBRANCA: 'Recobrança necessária',
  PAGA: 'Paga',
  PARCIALMENTE_PAGA: 'Parcialmente paga',
  NAO_GERADA: 'Não gerada',
  PARCIALMENTE_GERADA: 'Parcialmente gerada',
  EM_ABERTO: 'Em aberto',
  CANCELADA: 'Cancelada',
};

// POSSUI_VENCIDA -> 'red': mesma cor ja usada para VENCIDA em
// STATUS_COBRANCA_COLORS (CobrancasEventosAdmin.js) -- e' o precedente
// direto mais proximo no proprio modulo para "existe parcela vencida",
// preferido a 'gold' (reservado aqui para os estados parciais/mistos
// PARCIALMENTE_PAGA/PARCIALMENTE_GERADA).
const STATUS_RESUMIDO_COLORS = {
  REVISAO_NECESSARIA: 'red',
  VERIFICACAO_PENDENTE: 'processing',
  CANCELAMENTO_EM_ANDAMENTO: 'processing',
  POSSUI_VENCIDA: 'red',
  // MESMA cor da tag individual RECOBRANCA_NECESSARIA (laranja, ver
  // STATUS_COBRANCA_COLORS em CobrancasEventosAdmin.js): exige acao,
  // nunca sucesso (verde) nem historico/inativo (cinza).
  POSSUI_RECOBRANCA: 'orange',
  PAGA: 'green',
  PARCIALMENTE_PAGA: 'gold',
  NAO_GERADA: 'default',
  PARCIALMENTE_GERADA: 'gold',
  EM_ABERTO: 'blue',
  CANCELADA: 'default',
};

/** Label PT-BR de `status_resumido` (fallback: o proprio codigo, nunca
 * quebra se o backend introduzir um codigo novo antes do frontend). */
export function labelStatusResumido(statusResumido) {
  return STATUS_RESUMIDO_LABELS[statusResumido] || statusResumido;
}

/** Cor de Tag do antd para `status_resumido` (fallback: 'default'). */
export function corStatusResumido(statusResumido) {
  return STATUS_RESUMIDO_COLORS[statusResumido] || 'default';
}

// Ordem de prioridade do resumo compacto (Secao 8 da instrucao) -- segue
// a MESMA precedencia conceitual que o backend usa para decidir
// status_resumido (conflito > incerto > cancelando > vencida > aberta >
// paga > cancelada > sem_cobranca), para o texto nunca "contradizer"
// visualmente a Tag de status_resumido mostrada ao lado.
const ORDEM_RESUMO_PARCELAS = [
  { chave: 'quantidade_com_conflito_pagamento', singular: 'com conflito de pagamento', plural: 'com conflito de pagamento', todas: 'todas com conflito de pagamento' },
  { chave: 'quantidade_com_resultado_incerto', singular: 'com resultado incerto', plural: 'com resultado incerto', todas: 'todas com resultado incerto' },
  { chave: 'quantidade_cancelando', singular: 'com cancelamento em andamento', plural: 'com cancelamento em andamento', todas: 'todas com cancelamento em andamento' },
  { chave: 'quantidade_vencidas', singular: 'vencida', plural: 'vencidas', todas: 'todas vencidas' },
  { chave: 'quantidade_abertas', singular: 'em aberto', plural: 'em aberto', todas: 'todas em aberto' },
  { chave: 'quantidade_pagas', singular: 'paga', plural: 'pagas', todas: 'todas pagas' },
  { chave: 'quantidade_canceladas', singular: 'cancelada', plural: 'canceladas', todas: 'todas canceladas' },
  { chave: 'quantidade_sem_cobranca', singular: 'sem cobrança gerada', plural: 'sem cobrança gerada', todas: 'todas sem cobrança gerada' },
];

/**
 * Monta o resumo textual compacto de um grupo (Secao 8 da instrucao):
 * "3 parcelas · 2 em aberto · 1 paga". Usa SOMENTE os campos de
 * contagem ja calculados pelo backend (`quantidade_*`) -- nunca itera
 * `grupo.parcelas` para recalcular nada. Singular/plural tratado tanto
 * no total ("1 parcela"/"2 parcelas") quanto em cada contagem ("1
 * vencida"/"2 vencidas"). Quando todas as parcelas caem no MESMO balde,
 * colapsa para a forma mais simples ("3 parcelas · todas pagas") em vez
 * de repetir a contagem duas vezes. Campos nullable (Secao 9): qualquer
 * `quantidade_*`/`quantidade_parcelas` ausente/null e' tratado como 0.
 */
export function resumoCompactoParcelas(grupo) {
  const contagens = ORDEM_RESUMO_PARCELAS.map(({ chave, ...resto }) => ({
    ...resto,
    valor: Number(grupo?.[chave]) || 0,
  }));
  const somaContagens = contagens.reduce((soma, item) => soma + item.valor, 0);
  const total = grupo?.quantidade_parcelas != null ? Number(grupo.quantidade_parcelas) : somaContagens;

  if (!total) {
    return '0 parcelas';
  }

  const textoTotal = `${total} parcela${total === 1 ? '' : 's'}`;
  const naoZerados = contagens.filter((item) => item.valor > 0);

  if (naoZerados.length === 1 && naoZerados[0].valor === total) {
    return `${textoTotal} · ${naoZerados[0].todas}`;
  }

  const segmentos = naoZerados.map((item) => `${item.valor} ${item.valor === 1 ? item.singular : item.plural}`);
  return segmentos.length ? `${textoTotal} · ${segmentos.join(' · ')}` : textoTotal;
}

/**
 * Fase Eventos-Recobranca 3 -- uma parcela e' RECOBRAVEL INDIVIDUALMENTE
 * ("Gerar nova cobrança") quando esta em RECOBRANCA_NECESSARIA (a unica
 * charge foi cancelada) E o backend ja' sinaliza `acao_financeira ===
 * GERAR_COBRANCA` (inscricao EFETIVADA / participacao CONFIRMADA /
 * evento ATIVO / sessao AGENDADA). Fonte semantica EXCLUSIVA do backend
 * (`acao_financeira` + `status_cobranca`), NUNCA reconstruida. NUNCA e'
 * uma acao de lote (recobranca e' SOMENTE individual nesta versao, ver
 * `grupoElegivelParaGerarCobranca` abaixo).
 */
export function parcelaElegivelParaRecobranca(parcela) {
  if (!parcela) {
    return false;
  }
  return (
    parcela.acao_financeira === ACAO_FINANCEIRA.GERAR_COBRANCA &&
    parcela.status_cobranca === 'RECOBRANCA_NECESSARIA'
  );
}

/**
 * Decide se o botao "Gerar cobranças" deve aparecer no NIVEL DO GRUPO
 * (Secao 16 da instrucao) -- e, pela MESMA funcao, a elegibilidade de
 * SELECAO EM MASSA (rowSelection). Verifica se ALGUMA parcela do grupo
 * esta com `acao_financeira === GERAR_COBRANCA`.
 *
 * Fase Eventos-Recobranca 3 -- EXCLUSAO DELIBERADA da recobranca do fluxo
 * em massa (Passo 7): uma parcela RECOBRANCA_NECESSARIA tambem carrega
 * `acao_financeira === GERAR_COBRANCA`, mas recobranca e' SOMENTE
 * INDIVIDUAL nesta versao. Portanto o grupo so' e' elegivel para a acao
 * de grupo/lote quando tem ao menos uma parcela GENUINAMENTE SEM_COBRANCA
 * (geracao inicial normal) -- um grupo composto SO por recobrancas NUNCA
 * entra na selecao em massa nem mostra o botao de grupo (a recobranca
 * dessas parcelas vive no botao "Gerar nova cobrança" por linha).
 */
export function grupoElegivelParaGerarCobranca(grupo) {
  if (!grupo || !Array.isArray(grupo.parcelas)) {
    return false;
  }
  return grupo.parcelas.some(
    (parcela) =>
      parcela.acao_financeira === ACAO_FINANCEIRA.GERAR_COBRANCA &&
      parcela.status_cobranca === 'SEM_COBRANCA'
  );
}

// ---------------------------------------------------------------------
// Fase 3A.2 -- helpers puros novos para a operacao em lote "Gerar
// cobrancas selecionadas" (POST /api/eventos/inscricoes/gerar-cobrancas-
// lote, backend Fase 3A.1 ja mergeado). Mesmo principio de todo o
// modulo: nenhuma regra financeira nova e' inventada aqui -- a
// elegibilidade de selecao reaproveita `grupoElegivelParaGerarCobranca`
// (ja existente acima, MESMO sinal do botao individual "Gerar
// cobrancas"), e as contagens do resultado (`total_sucesso`/
// `total_ja_existente`/`total_ignorado`/`total_erro`) sao usadas
// exatamente como o backend devolve, nunca recalculadas.

/** Teto de inscricoes por lote -- mesmo valor do `max_length=100` do
 * schema backend (`GerarCobrancasLoteRequest`, Fase 3A.1). Defesa
 * client-side additiva: o backend already valida isso de qualquer
 * forma, nunca a unica linha de defesa. */
export const LIMITE_LOTE_GERAR_COBRANCAS = 100;

/** Confirma se uma selecao (array de evento_inscricao_id) respeita o
 * teto do lote -- usado no `onChange` do rowSelection para rejeitar
 * selecoes acima do limite antes de qualquer chamada ao backend. */
export function selecaoDentroDoLimiteLote(chaves, limite = LIMITE_LOTE_GERAR_COBRANCAS) {
  return (chaves || []).length <= limite;
}

/**
 * Soma `quantidade_sem_cobranca` (campo ja calculado pelo backend, ver
 * GET /api/eventos/cobrancas/agrupado) dos grupos EXPLICITAMENTE
 * selecionados -- usada SOMENTE como contagem estimada no modal de
 * confirmacao ("Serao materializadas ate X cobrancas"), nunca como
 * nova regra de elegibilidade. Como a selecao nunca cruza paginas
 * (Secao 6/21 da instrucao), todo grupo selecionado esta sempre
 * presente em `grupos` (a pagina atual carregada) -- nenhum fetch
 * adicional e' necessario para calcular isto.
 */
export function calcularQuantidadeEstimadaLote(grupos, chavesSelecionadas) {
  const selecionados = new Set(chavesSelecionadas || []);
  return (grupos || [])
    .filter((grupo) => selecionados.has(grupo.evento_inscricao_id))
    .reduce((soma, grupo) => soma + (Number(grupo.quantidade_sem_cobranca) || 0), 0);
}

/**
 * Filtra SOMENTE os itens problematicos (IGNORADO/ERRO) do resultado do
 * lote -- usado para montar a tabela de detalhes (Secao 14 da
 * instrucao: "mostrar tabela/lista de detalhes APENAS dos itens
 * problematicos"), nunca lista os SUCESSO (idempotentes ou nao).
 */
export function resultadosProblematicosLote(resultadoLote) {
  if (!resultadoLote || !Array.isArray(resultadoLote.resultados)) {
    return [];
  }
  return resultadoLote.resultados.filter((item) => item.status === 'IGNORADO' || item.status === 'ERRO');
}

/**
 * Decide se o lote concluiu sem nenhum item problematico -- fonte
 * EXCLUSIVA: `total_ignorado`/`total_erro` devolvidos pelo backend
 * (nunca recalculados a partir de `resultados`). `total_ja_existente >
 * 0` NUNCA torna isto `false` -- e' sucesso idempotente (Secao 15 da
 * instrucao), nao falha.
 */
export function loteConcluidoSemProblemas(resultadoLote) {
  if (!resultadoLote) {
    return false;
  }
  return (resultadoLote.total_ignorado || 0) === 0 && (resultadoLote.total_erro || 0) === 0;
}

/**
 * Poda `chavesExpandidas` apos um refresh (Secao 10/18 da instrucao):
 * mantem expandido todo grupo cujo `evento_inscricao_id` ainda esteja
 * presente na pagina recem-carregada; remove silenciosamente os que
 * sairam (sem lancar erro nem deixar uma linha "expandida fantasma").
 * Usado APENAS no caminho de refresh pos-acao financeira -- filtro/
 * paginacao tratam o reset de outra forma (ver CobrancasEventosAdmin.js).
 */
export function chavesExpandidasAposRecarregar(chavesAtuais, idsPresentesNaPagina) {
  const presentes = new Set(idsPresentesNaPagina || []);
  return (chavesAtuais || []).filter((chave) => presentes.has(chave));
}

// ---------------------------------------------------------------------
// Fase 3B.2 -- helpers puros novos para a operacao em lote "Emitir
// boletos selecionados" (POST /api/eventos/cobrancas/emitir-boletos-
// lote, backend Fase 3B.1 ja mergeado, PR #48). Mesmo principio de todo
// o modulo: nenhuma regra financeira nova e' inventada aqui.
//
// DIFERENCA DE UNIDADE vs a Fase 3A.2 (materializacao em lote): ali a
// selecao e' por GRUPO (evento_inscricao_id, tabela pai). Aqui a
// selecao e' por PARCELA/FinancialCharge (financial_charge_id, dentro
// da subtabela de cada grupo) -- a selecao pode abranger parcelas de
// grupos/alunos diferentes na mesma pagina, entao o estado precisa ser
// GLOBAL (nao por grupo) e as funcoes abaixo operam sobre arrays de
// `financial_charge_id`, nunca sobre `evento_inscricao_id`.
//
// Elegibilidade: fonte semantica EXCLUSIVA `parcela.acao_financeira ===
// EMITIR_BOLETO` + `parcela.financial_charge_id` presente -- o MESMO
// sinal ja usado pelo botao individual "Emitir boleto" (ver
// `mostrarBotaoEmitir` em CobrancasEventosAdmin.js), nunca reconstruida
// por status_cobranca/status_emissao/status_participacao/status_
// inscricao/provider/attempt.

/** Teto de FinancialCharges por lote de emissao -- mesmo valor do
 * `max_length=10` do schema backend (`EmitirBoletosLoteRequest`, Fase
 * 3B.1). Defesa client-side additiva: o backend ja valida isso de
 * qualquer forma, nunca a unica linha de defesa. */
export const LIMITE_LOTE_EMITIR_BOLETOS = 10;

/** Fonte semantica EXCLUSIVA para decidir se uma parcela pode ser
 * SELECIONADA para o lote de emissao -- identica a condicao que ja
 * habilita o botao individual "Emitir boleto" (Secao "AUTORIDADE DE
 * ELEGIBILIDADE" da instrucao). Nunca inferida por status_cobranca/
 * status_emissao/provider/linha_digitavel/codigo_barras/attempt. */
export function parcelaElegivelParaEmitirBoletoLote(parcela) {
  return parcela?.acao_financeira === ACAO_FINANCEIRA.EMITIR_BOLETO && Boolean(parcela.financial_charge_id);
}

/** `financial_charge_id` de TODAS as parcelas elegiveis de um grupo --
 * usado tanto para a conveniencia "selecionar elegiveis do grupo"
 * quanto para a poda de selecao pos-refresh abaixo. */
export function idsElegiveisDoGrupo(grupo) {
  return (grupo?.parcelas || [])
    .filter(parcelaElegivelParaEmitirBoletoLote)
    .map((parcela) => parcela.financial_charge_id);
}

/** `financial_charge_id` de TODAS as parcelas de um grupo (elegiveis ou
 * nao) -- usado somente para desmarcar de forma completa e previsivel
 * (nunca deixa um id "orfao" selecionado se ele deixou de ser elegivel
 * entre a selecao e o clique de desmarcar). */
function idsDoGrupo(grupo) {
  return (grupo?.parcelas || [])
    .map((parcela) => parcela.financial_charge_id)
    .filter((id) => id != null);
}

// Validacao do teto de selecao reutiliza `selecaoDentroDoLimiteLote`
// (Fase 3A.2, ja generica sobre o array + limite) diretamente nos
// pontos de uso -- basta passar `LIMITE_LOTE_EMITIR_BOLETOS` como
// segundo argumento, sem duplicar/renomear a funcao aqui.

/**
 * "Selecionar todas as elegiveis do grupo" (Secao "SELECIONAR
 * ELEGIVEIS DO GRUPO" da instrucao) -- funcao PURA, sem side-effect:
 * recebe a selecao GLOBAL atual + o grupo clicado e devolve a proxima
 * selecao. REGRA DE PRODUTO (nao negociavel): se adicionar todas as
 * elegiveis ainda nao selecionadas fizer a selecao global ultrapassar
 * o limite, a operacao inteira e' RECUSADA -- nunca adiciona
 * parcialmente, nunca escolhe automaticamente um subconjunto, nunca
 * trunca silenciosamente (`recusado: true`, selecao devolvida
 * inalterada). Se todas as elegiveis do grupo ja estiverem
 * selecionadas, e' um no-op idempotente (`adicionados: 0,
 * recusado: false`).
 */
export function selecionarElegiveisDoGrupo(selecaoAtual, grupo, limite = LIMITE_LOTE_EMITIR_BOLETOS) {
  const atuais = selecaoAtual || [];
  const aAdicionar = idsElegiveisDoGrupo(grupo).filter((id) => !atuais.includes(id));

  if (aAdicionar.length === 0) {
    return { selecao: atuais, adicionados: 0, recusado: false };
  }

  const novaSelecao = [...atuais, ...aAdicionar];
  if (novaSelecao.length > limite) {
    return { selecao: atuais, adicionados: 0, recusado: true };
  }

  return { selecao: novaSelecao, adicionados: aAdicionar.length, recusado: false };
}

/** Desmarca todas as parcelas do grupo presentes na selecao GLOBAL
 * atual -- contraparte previsivel de `selecionarElegiveisDoGrupo`
 * (nunca ambiguo: remove exatamente os ids que pertencem a este grupo,
 * elegiveis ou nao, para nunca deixar um checkbox "fantasma"). */
export function desmarcarTodasDoGrupo(selecaoAtual, grupo) {
  const idsGrupo = new Set(idsDoGrupo(grupo));
  return (selecaoAtual || []).filter((id) => !idsGrupo.has(id));
}

/**
 * Soma visual (Secao "UX DE SELECAO" da instrucao) do `valor` das
 * parcelas EXPLICITAMENTE selecionadas, entre TODOS os grupos da
 * pagina atual -- SOMENTE apresentacao no modal/barra de selecao,
 * NUNCA usada para decisao financeira (o backend nunca recebe este
 * numero). `valor` chega como STRING Decimal (mesma convencao de
 * `formatarValorBRL` em CobrancasEventosAdmin.js) -- `Number()` aqui e'
 * so' para somar/exibir.
 */
export function calcularValorSelecionadoEmissaoLote(grupos, chargesSelecionadas) {
  const selecionados = new Set(chargesSelecionadas || []);
  let soma = 0;
  (grupos || []).forEach((grupo) => {
    (grupo?.parcelas || []).forEach((parcela) => {
      if (parcela.financial_charge_id != null && selecionados.has(parcela.financial_charge_id)) {
        soma += Number(parcela.valor) || 0;
      }
    });
  });
  return soma;
}

/**
 * Poda a selecao GLOBAL apos um refresh da listagem (Secao "REFRESH
 * APOS O LOTE" da instrucao): remove qualquer `financial_charge_id`
 * que NAO exista mais entre as parcelas elegiveis da pagina recem-
 * carregada -- cobre os DOIS motivos do requisito na MESMA passada
 * (a parcela desapareceu da pagina OU deixou de ter `acao_financeira
 * === EMITIR_BOLETO`, ex.: uma emissao SUCESSO), sem distinguir os
 * dois casos porque o resultado e' identico: sai da selecao. Nunca
 * lanca erro com entradas ausentes/malformadas.
 */
export function podarSelecaoEmissaoLote(chargesSelecionadas, grupos) {
  const elegiveisPresentes = new Set();
  (grupos || []).forEach((grupo) => {
    idsElegiveisDoGrupo(grupo).forEach((id) => elegiveisPresentes.add(id));
  });
  return (chargesSelecionadas || []).filter((id) => elegiveisPresentes.has(id));
}

/**
 * Filtra SOMENTE os itens problematicos (IGNORADO/INCERTO/ERRO) do
 * resultado do lote de EMISSAO -- contraparte de
 * `resultadosProblematicosLote` (materializacao, Fase 3A.2), mas
 * DELIBERADAMENTE NAO reutilizada dali: o contrato de emissao tem uma
 * categoria a mais (`INCERTO`, Fase 3B.1) que a materializacao nunca
 * teve, e INCERTO e' problematico aqui (nunca deve cair no "sucesso").
 * `JA_EMITIDO` NUNCA entra (e' sucesso idempotente, Secao "RESULTADO DO
 * LOTE" da instrucao), assim como `SUCESSO`.
 */
export function resultadosProblematicosLoteEmissao(resultadoLote) {
  if (!resultadoLote || !Array.isArray(resultadoLote.resultados)) {
    return [];
  }
  return resultadoLote.resultados.filter(
    (item) => item.status === 'IGNORADO' || item.status === 'INCERTO' || item.status === 'ERRO'
  );
}

/**
 * Decide se o lote de EMISSAO concluiu sem nenhum item problematico --
 * fonte EXCLUSIVA: `total_ignorado`/`total_incerto`/`total_erro`
 * devolvidos pelo backend (nunca recalculados a partir de
 * `resultados`). DIFERENTE de `loteConcluidoSemProblemas`
 * (materializacao): aqui `total_incerto > 0` TAMBEM torna isto
 * `false` -- INCERTO nunca pode ser apresentado como "operacao
 * concluida sem problemas" (Secao "RESULTADO DO LOTE" da instrucao).
 * `total_ja_emitido > 0` NUNCA torna isto `false` -- e' sucesso
 * idempotente, nao falha.
 */
export function loteEmissaoConcluidoSemProblemas(resultadoLote) {
  if (!resultadoLote) {
    return false;
  }
  return (
    (resultadoLote.total_ignorado || 0) === 0 &&
    (resultadoLote.total_incerto || 0) === 0 &&
    (resultadoLote.total_erro || 0) === 0
  );
}

// `status` (nao `codigo`) e' a UNICA fonte de classificacao visual dos
// 5 valores reais do contrato (Secao "RESULTADO DO LOTE" da instrucao:
// "Nao decidir semantica visual pelo codigo"). `codigo`/`mensagem` sao
// usados apenas como DETALHE textual (a `mensagem` ja vem pronta e
// segura do backend, exibida tal como recebida -- nunca reconstruida a
// partir do `codigo`, nunca um mapa de 19 entradas duplicando
// conhecimento do backend).
const STATUS_ITEM_LOTE_EMISSAO_LABELS = {
  SUCESSO: 'Emitido agora',
  JA_EMITIDO: 'Já emitido',
  IGNORADO: 'Não elegível',
  INCERTO: 'Resultado incerto',
  ERRO: 'Erro',
};

const STATUS_ITEM_LOTE_EMISSAO_COLORS = {
  SUCESSO: 'green',
  JA_EMITIDO: 'default',
  IGNORADO: 'gold',
  INCERTO: 'orange',
  ERRO: 'red',
};

/** Label PT-BR de `status` de um item do lote de emissao (fallback: o
 * proprio status, nunca quebra se o backend introduzir um valor novo
 * antes do frontend). */
export function labelStatusItemLoteEmissao(status) {
  return STATUS_ITEM_LOTE_EMISSAO_LABELS[status] || status;
}

/** Cor de Tag do antd para `status` de um item do lote de emissao
 * (fallback: 'default'). */
export function corStatusItemLoteEmissao(status) {
  return STATUS_ITEM_LOTE_EMISSAO_COLORS[status] || 'default';
}

// ---------------------------------------------------------------------
// Fase D.3 -- helpers puros novos para "Cobrancas Consolidadas de
// Eventos" (sugestao de consolidacao via `chave_consolidacao` + Groups
// ja formados via `id_financial_charge_group`/`estado_documento_grupo`/
// `acoes_permitidas_grupo`, todos campos D.1/D.2/D.3a ja expostos por
// GET /api/eventos/cobrancas/agrupado DENTRO de `grupo.parcelas[]` --
// NUNCA no nivel do card pai da inscricao).
//
// PRINCIPIO CENTRAL (Secao 3 da instrucao, o mesmo de todo o modulo): o
// frontend NUNCA reconstroi compatibilidade financeira (aluno+unidade+
// responsavel+CPF+vencimento) -- a UNICA fonte de agrupamento e' a
// igualdade de `chave_consolidacao`, uma string opaca ja calculada pelo
// backend. `POST /grupos` continua sendo a UNICA autoridade que
// revalida tudo sob lock; a chave nunca e' enviada a nenhum endpoint,
// so' `financial_charge_ids`.
//
// D.3 implementa SOMENTE `DISSOLVER` como acao real -- `EMITIR`/
// `CANCELAR`/`VERIFICAR_PAGAMENTO`/`VERIFICAR_CANCELAMENTO`/`REEMITIR`
// ficam para D.4/D.5 mesmo quando presentes em `acoes_permitidas_grupo`
// (`grupoTemAcao` abaixo e' generico -- quem decide QUAIS acoes viram
// botao nesta fase e' exclusivamente CobrancasEventosAdmin.js, nunca
// este modulo).

/** Achata `grupo.parcelas[]` de TODOS os `itens` (grupos visuais da
 * pagina agrupada) num unico array, preservando o contexto de
 * apresentacao do card pai (aluno/evento/sessao/inscricao) em cada
 * parcela -- necessario porque consolidacao pode cruzar cards/eventos
 * diferentes do MESMO aluno (Secao 6 da instrucao). Nunca modifica
 * `itens` (spread cria objetos novos, `flatMap` cria array novo).
 * Saida determinística (mesma ordem de `itens`/`parcelas`). */
export function achatarParcelasComContexto(itens) {
  return (itens || []).flatMap((grupo) =>
    (grupo?.parcelas || []).map((parcela) => ({
      ...parcela,
      evento_inscricao_id: grupo.evento_inscricao_id,
      aluno_nome: grupo.aluno_nome,
      evento_nome: grupo.evento_nome,
      sessao_data: grupo.sessao_data,
      // Fase E.1 (WhatsApp) -- responsavel financeiro pertence ao ALUNO
      // da inscricao (nivel do card pai), nunca ao FinancialChargeGroup
      // (que pode reunir parcelas de cards diferentes) -- mesmo motivo
      // de aluno_nome acima.
      responsavel_nome: grupo.responsavel_nome,
      responsavel_telefone: grupo.responsavel_telefone,
    }))
  );
}

/** Agrupa as parcelas ELEGIVEIS para sugestao (Secao 7 da instrucao)
 * por igualdade EXATA de `chave_consolidacao` -- nunca por aluno/
 * unidade/vencimento recalculados aqui. Elegibilidade: `chave_
 * consolidacao` presente, `financial_charge_id` presente, `id_
 * financial_charge_group` ausente (Charge ja agrupada nunca e'
 * sugerida de novo). Somente chaves com >= 2 candidatos viram
 * sugestao (uma unica Charge nunca e' "consolidavel" sozinha).
 * Funciona cross-evento/cross-card por construcao (opera sobre o
 * array ja achatado de TODOS os cards da pagina). Nunca modifica
 * `itens`. Saida determinística (Map preserva ordem de insercao). */
export function indexarSugestoesConsolidacao(itens) {
  const parcelas = achatarParcelasComContexto(itens);
  const porChave = new Map();
  parcelas.forEach((parcela) => {
    if (!parcela.chave_consolidacao) return;
    if (parcela.id_financial_charge_group != null) return;
    if (parcela.financial_charge_id == null) return;
    if (!porChave.has(parcela.chave_consolidacao)) {
      porChave.set(parcela.chave_consolidacao, []);
    }
    porChave.get(parcela.chave_consolidacao).push(parcela);
  });
  const sugestoes = [];
  porChave.forEach((componentes, chave) => {
    if (componentes.length >= 2) {
      sugestoes.push({ chave_consolidacao: chave, componentes });
    }
  });
  return sugestoes;
}

/** Teto de componentes por Group -- mesmo `max_length=10` do schema
 * backend (`FormarGrupoFinancialChargesRequest`). Usado para NUNCA
 * enviar mais de 10 ids no POST (Secao 8 da instrucao) -- defesa
 * client-side aditiva, o backend ja valida isso de qualquer forma. */
export const LIMITE_COMPONENTES_POR_GRUPO = 10;

/** Ordena os candidatos de uma sugestao deterministicamente
 * (vencimento asc, depois financial_charge_id asc -- `data_vencimento`
 * e' sempre "YYYY-MM-DD", comparacao lexicografica de string já' e'
 * comparacao cronologica correta) e recorta aos primeiros `limite`.
 * `restante` e' a contagem exata de candidatos excluidos desta rodada
 * -- eles permanecem elegiveis e reaparecerao como nova sugestao apos
 * o refetch seguinte (Secao 8 da instrucao: nunca enviar >10, nunca
 * escolher arbitrariamente quais entram). Nunca modifica `componentes`
 * (spread + sort sobre copia). */
export function limitarSugestaoATeto(componentes, limite = LIMITE_COMPONENTES_POR_GRUPO) {
  const ordenados = [...(componentes || [])].sort((a, b) => {
    if (a.data_vencimento !== b.data_vencimento) {
      return a.data_vencimento < b.data_vencimento ? -1 : 1;
    }
    return (a.financial_charge_id || 0) - (b.financial_charge_id || 0);
  });
  return {
    componentesExibidos: ordenados.slice(0, limite),
    restante: Math.max(0, ordenados.length - limite),
  };
}

/** Soma VISUAL (apresentacao apenas, Secao 12 da instrucao) do `valor`
 * dos componentes informados -- NUNCA tratado como valor financeiro
 * autoritativo (o backend recalcula/valida tudo na formacao/emissao
 * real). `valor` chega como STRING Decimal, mesma convencao de
 * `calcularValorSelecionadoEmissaoLote` (Fase 3B.2). */
export function calcularValorTotalVisual(componentes) {
  return (componentes || []).reduce((soma, c) => soma + (Number(c.valor) || 0), 0);
}

/** Agrupa as parcelas JA' AGRUPADAS (`id_financial_charge_group`
 * presente) por `id_financial_charge_group`, produzindo EXATAMENTE um
 * item por Group -- nunca repete um card por componente (Secao 17 da
 * instrucao). `estado_documento_grupo`/`acoes_permitidas_grupo` sao
 * IGUAIS em todas as parcelas do mesmo grupo por garantia do backend
 * (D.1/D.2, testado la') -- mas esta funcao NUNCA confia cegamente:
 * se encontrar uma divergencia real entre os componentes visiveis
 * nesta pagina (Secao 18 da instrucao), marca `inconsistente: true`,
 * zera `estado_documento_grupo`/`acoes_permitidas_grupo` (fail-closed
 * -- nunca escolhe um dos valores divergentes, nunca oferece DISSOLVER
 * nesse caso) em vez de quebrar a pagina ou inventar uma regra nova.
 * `linha_digitavel_grupo`/`codigo_barras_grupo` sao preservados do
 * primeiro componente encontrado (D.3 nao os renderiza ainda -- uso
 * futuro em D.4, Secao 29 da instrucao). Nunca deriva acoes -- vem
 * exclusivamente do backend. Nunca modifica `itens`. */
export function agruparPorGroupId(itens) {
  const parcelas = achatarParcelasComContexto(itens);
  const porGrupo = new Map();
  parcelas.forEach((parcela) => {
    if (parcela.id_financial_charge_group == null) return;
    if (!porGrupo.has(parcela.id_financial_charge_group)) {
      porGrupo.set(parcela.id_financial_charge_group, []);
    }
    porGrupo.get(parcela.id_financial_charge_group).push(parcela);
  });
  const grupos = [];
  porGrupo.forEach((componentes, groupId) => {
    const estadosDistintos = new Set(componentes.map((c) => c.estado_documento_grupo));
    const acoesDistintas = new Set(
      componentes.map((c) => [...(c.acoes_permitidas_grupo || [])].sort().join('|'))
    );
    const inconsistente = estadosDistintos.size > 1 || acoesDistintas.size > 1;
    const primeiro = componentes[0];
    grupos.push({
      id_financial_charge_group: groupId,
      componentes,
      estado_documento_grupo: inconsistente ? null : primeiro.estado_documento_grupo,
      acoes_permitidas_grupo: inconsistente ? [] : primeiro.acoes_permitidas_grupo || [],
      linha_digitavel_grupo: componentes.find((c) => c.linha_digitavel_grupo)?.linha_digitavel_grupo || null,
      codigo_barras_grupo: componentes.find((c) => c.codigo_barras_grupo)?.codigo_barras_grupo || null,
      // Fase E.1 (WhatsApp) -- `url_boleto_grupo` mesmo padrao dos 2
      // identificadores acima. `valor_total_grupo`/`quantidade_
      // cobrancas_grupo` sao CANONICOS (calculados no backend a partir
      // do Group COMPLETO, nunca so' dos componentes visiveis nesta
      // pagina) -- iguais em todos os componentes por garantia do
      // backend, lidos do primeiro. `responsavel_nome`/`responsavel_
      // telefone` idem (mesmo aluno em todo o grupo, formacao exige
      // MESMO responsavel financeiro).
      url_boleto_grupo: componentes.find((c) => c.url_boleto_grupo)?.url_boleto_grupo || null,
      valor_total_grupo: primeiro.valor_total_grupo ?? null,
      quantidade_cobrancas_grupo: primeiro.quantidade_cobrancas_grupo ?? null,
      responsavel_nome: primeiro.responsavel_nome || null,
      responsavel_telefone: primeiro.responsavel_telefone || null,
      inconsistente,
    });
  });
  return grupos;
}

// Vocabulario fechado -- MESMOS 11 estados do read-model backend
// (financial_charge_group_estado_leitura_service.ESTADOS_DOCUMENTO_
// GRUPO_VALIDOS). Fallback para o proprio codigo (nunca quebra se o
// backend introduzir um estado novo antes do frontend, mesmo padrao ja
// usado por `labelStatusResumido`/`STATUS_RESUMIDO_LABELS` acima).
const ESTADO_GRUPO_LABELS = {
  GRUPO_SEM_EMISSAO: 'Aguardando emissão',
  // Fase Eventos-Recobranca 4 -- Attempt PENDENTE nunca enviado: pronto
  // para (re)emitir pela rota normal. NUNCA "Emitindo..." (nao ha envio
  // em andamento) -- corrige o dead-end operacional.
  PRONTO_PARA_EMITIR: 'Pronto para emitir',
  EMITINDO: 'Emitindo...',
  EMISSAO_INCERTA: 'Emissão requer verificação',
  EMISSAO_BLOQUEADA: 'Emissão bloqueada — requer análise',
  EMITIDO: 'Boleto emitido',
  REEMITIDO: 'Boleto reemitido',
  PAGO: 'Pago',
  CANCELANDO: 'Cancelamento em andamento',
  CANCELAMENTO_INCERTO: 'Cancelamento requer verificação',
  CONFLITO_PAGAMENTO: 'Requer análise manual',
  CANCELADO_ELEGIVEL_REEMISSAO: 'Cancelado — pode ser reemitido',
};

const ESTADO_GRUPO_COLORS = {
  GRUPO_SEM_EMISSAO: 'default',
  // Azul: acionavel (ha' uma acao "Emitir boleto" disponivel), distinto
  // do 'processing' de EMITINDO (que sugere algo em andamento).
  PRONTO_PARA_EMITIR: 'blue',
  EMITINDO: 'processing',
  EMISSAO_INCERTA: 'gold',
  EMISSAO_BLOQUEADA: 'red',
  EMITIDO: 'green',
  REEMITIDO: 'green',
  PAGO: 'green',
  CANCELANDO: 'processing',
  CANCELAMENTO_INCERTO: 'gold',
  CONFLITO_PAGAMENTO: 'red',
  CANCELADO_ELEGIVEL_REEMISSAO: 'default',
};

/** Label PT-BR de `estado_documento_grupo` (fallback: o proprio codigo
 * quando desconhecido; "Requer atualização" quando `null`/ausente --
 * caso de `agruparPorGroupId` marcar `inconsistente: true`, Secao 18 da
 * instrucao). Nunca lanca, nunca quebra o render. */
export function labelEstadoGrupo(estado) {
  if (!estado) return 'Requer atualização';
  return ESTADO_GRUPO_LABELS[estado] || estado;
}

/** Cor de Tag do antd para `estado_documento_grupo` (fallback:
 * 'default'). */
export function corEstadoGrupo(estado) {
  return ESTADO_GRUPO_COLORS[estado] || 'default';
}

/** Fonte semantica EXCLUSIVA para decidir se uma acao de Group deve
 * aparecer como botao (Secao 21 da instrucao) -- NUNCA inferida pelo
 * `estado_documento_grupo` (label e' so' apresentacao, nunca
 * autorizacao). `grupo.acoes_permitidas_grupo` ausente/nao-array
 * (defesa contra `inconsistente: true` de `agruparPorGroupId`, que
 * zera para `[]`) nunca quebra -- so' retorna `false`. */
export function grupoTemAcao(grupo, acao) {
  return Array.isArray(grupo?.acoes_permitidas_grupo) && grupo.acoes_permitidas_grupo.includes(acao);
}

// ---------------------------------------------------------------------
// Fase D.4b -- operacoes do DOCUMENTO consolidado (EMITIR/REEMITIR/
// VERIFICAR_PAGAMENTO/CANCELAR/VERIFICAR_CANCELAMENTO). Mesmo principio
// de tudo acima: `grupoTemAcao` (ja definida) continua sendo a UNICA
// fonte de autorizacao de botao -- as funcoes abaixo so' preparam TEXTO/
// DADOS de apresentacao, nunca decidem se uma acao pode aparecer.

/** Mapeia `resultado` retornado por POST .../verificar-pagamento (Fase
 * C.3c/D.4a) para o feedback a exibir ao admin. Vocabulario fechado (5
 * valores, ver VerificarPagamentoGrupoResponse no backend) -- NUNCA
 * afirma "Pago" so' porque o HTTP respondeu 200: o estado real
 * (inclusive PAGO) vem sempre do refetch de GET /agrupado que segue
 * esta chamada, nunca desta mensagem. */
export function mensagemFeedbackVerificarPagamentoGrupo(resultado) {
  switch (resultado) {
    case 'PAGAMENTO_CONFIRMADO_E_APLICADO':
      return { nivel: 'success', texto: 'Pagamento confirmado no provedor e aplicado às cobranças do grupo.' };
    case 'NAO_PAGO_NO_PROVIDER':
      return { nivel: 'info', texto: 'O provedor ainda não confirma o pagamento deste boleto.' };
    case 'NAO_FOI_POSSIVEL_CONFIRMAR':
      return {
        nivel: 'warning',
        texto: 'Não foi possível confirmar a situação no provedor agora. Tente novamente mais tarde.',
      };
    case 'CONFLITO_LOCAL':
      return {
        nivel: 'error',
        texto: 'A situação local deste grupo impede a aplicação do pagamento. A situação requer revisão manual.',
      };
    case 'ATTEMPT_INELEGIVEL':
      return { nivel: 'warning', texto: 'Não há um documento vigente elegível para verificação de pagamento.' };
    default:
      // Resposta desconhecida -- fail closed, nunca afirma sucesso.
      return { nivel: 'warning', texto: 'Não foi possível confirmar o resultado. Verifique a situação do grupo.' };
  }
}

/** Extrai os dados de apresentacao (aluno/vencimento/quantidade/valor)
 * que a confirmacao de EMITIR/REEMITIR precisa mostrar -- PURA, sem
 * formatacao de data/moeda (responsabilidade do componente, mesmo
 * padrao ja usado por SecaoCobrancasConsolidadas/ConsolidarCobrancasModal).
 *
 * Auditoria pos-D.4b (Secao "AJUSTE 2"): o read-model de GET /agrupado
 * (InscricaoCobrancaAgrupadaItem/ParcelaCobrancaAgrupadaResponse, ver
 * app/services/evento_cobranca_admin_service.py) NAO expoe nenhum
 * campo CANONICO de quantidade/valor no nivel do FinancialChargeGroup
 * -- os UNICOS 4 campos "_grupo" existentes sao estado_documento_grupo/
 * acoes_permitidas_grupo/linha_digitavel_grupo/codigo_barras_grupo.
 * `grupo.componentes` (`agruparPorGroupId`) e' somente o subconjunto de
 * FinancialCharges do Group que estao VISIVEIS NA PAGINA atual da
 * listagem paginada -- um Group pode ter componentes espalhados por
 * cards/paginas diferentes (consolidacao cross-evento/cross-card, ver
 * docstring de `agruparPorGroupId`). Por isso os campos abaixo sao
 * nomeados EXPLICITAMENTE como "...VisivelNestaPagina" -- nunca
 * `quantidade`/`valorTotal` puros -- para que NENHUM consumidor possa
 * apresenta-los como se fossem o total canonico do Group sem
 * qualificacao. Se um campo canonico real for adicionado ao backend no
 * futuro, ele deve ser adicionado aqui como um campo SEPARADO (ex.:
 * `quantidadeCanonica`), nunca substituindo silenciosamente estes.
 *
 * `valorVisivelNestaPagina` reaproveita `calcularValorTotalVisual`
 * (mesma soma VISUAL ja usada no card do grupo -- nunca autoritativa,
 * o backend recalcula tudo sob lock na emissao real). Nunca lanca --
 * `grupo`/`componentes` ausentes produzem um resumo vazio/zerado em
 * vez de quebrar o render. */
export function resumoConfirmacaoDocumentoGrupo(grupo) {
  const componentes = grupo?.componentes || [];
  const primeiro = componentes[0];
  return {
    alunoNome: primeiro?.aluno_nome || null,
    dataVencimento: primeiro?.data_vencimento || null,
    quantidadeVisivelNestaPagina: componentes.length,
    valorVisivelNestaPagina: calcularValorTotalVisual(componentes),
  };
}

// ---------------------------------------------------------------------
// Fase E.1 -- ENVIAR POR WHATSAPP (Eventos, individual e consolidado).
// Mesmo padrao ja usado em Faturas (FaturasList.js:
// normalizePhoneToWhatsApp/montarDadosCompartilhamentoFatura/
// enviarFaturaParaCliente) -- reimplementado aqui (nunca importado
// cruzado) porque a fonte de dados e' inteiramente diferente (modelo
// canonico FinancialCharge/FinancialChargeGroup, nunca Fatura/
// AsaasCobranca, ver investigacao E.1). Autorizacao do BOTAO nunca vive
// aqui -- estas funcoes so' preparam texto/decidem "ha dado suficiente
// para enviar", nunca decidem se a acao e' permitida pelo backend
// (nao ha' mutacao nenhuma aqui, WhatsApp e' 100% client-side).

/** Hotfix producao (Eventos > Cobrancas, tela branca) -- `valor_total_
 * grupo` (e qualquer outro campo `Decimal` do read-model) chega do
 * backend como STRING no JSON (Pydantic v2 serializa `Decimal` como
 * string em modo JSON, ex. `"139.49"`, nunca como numero -- confirmado
 * empiricamente: `model_dump(mode="json")`), nao como `number`. Mesma
 * convencao ja usada em `CobrancasEventosAdmin.js::formatarValorBRL`
 * (nao existe utilitario monetario compartilhado no projeto -- cada
 * tela do modulo Eventos formata localmente, ver comentario la').
 * `Number(valor)` aceita number/string numerica identicamente. Nunca
 * lanca, nunca coage null/undefined/invalido para "R$ 0,00" (isso
 * apresentaria uma cobranca financeira aparentemente valida onde na
 * verdade o dado esta ausente/invalido) -- placeholder `'—'` em vez
 * disso. */
export function formatarValorMonetarioBRL(valor) {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor === 'string' && valor.trim() === '') return '—';
  const numero = Number(valor);
  if (Number.isNaN(numero)) return '—';
  return `R$ ${numero.toFixed(2).replace('.', ',')}`;
}

/** Mesma normalizacao ja usada em Faturas -- extrai digitos, remove
 * zeros a esquerda, prefixa DDI 55 se ausente. `''` quando o telefone
 * e' vazio/ausente (nunca abre wa.me com numero vazio). */
export function normalizarTelefoneWhatsApp(telefone) {
  let normalizado = String(telefone || '').replace(/\D/g, '');
  normalizado = normalizado.replace(/^0+/, '');
  if (!normalizado) return '';
  if (!normalizado.startsWith('55')) {
    normalizado = `55${normalizado}`;
  }
  return normalizado;
}

/** URL so' conta como presente quando e' string com conteudo real apos
 * trim -- evita tratar whitespace-only como link valido (auditoria
 * final E.1, Secao 2/8: nem backend nem frontend faziam trim). Nunca
 * valida dominio/formato, so' descarta vazio/so-espacos. `null` quando
 * nao ha' URL util. */
function normalizarUrlBoleto(url) {
  if (typeof url !== 'string') return null;
  const aparada = url.trim();
  return aparada.length > 0 ? aparada : null;
}

/** Monta a mensagem de WhatsApp para um boleto INDIVIDUAL de Evento.
 * Nunca inclui CPF/ID interno/externalReference/payment id -- somente
 * aluno/evento/valor/vencimento/link/linha digitavel, todos ja
 * confiaveis (vindos do read-model, nunca recalculados aqui). Omite
 * URL/linha digitavel quando ausentes (nunca inventa/reconstroi). */
export function construirMensagemWhatsAppIndividual({
  alunoNome, eventoNome, valorFormatado, vencimentoFormatado, urlBoleto, linhaDigitavel,
}) {
  const urlValida = normalizarUrlBoleto(urlBoleto);
  const linhas = [
    'Olá! 😊',
    '',
    `Segue a cobrança referente à participação de ${alunoNome || 'aluno(a)'} no evento ${eventoNome || ''} da Fisicore.`,
    '',
    `Valor: ${valorFormatado}`,
    `Vencimento: ${vencimentoFormatado}`,
  ];
  if (urlValida) {
    linhas.push('', 'Acesse o boleto:', urlValida);
  }
  if (linhaDigitavel) {
    linhas.push('', 'Linha digitável:', linhaDigitavel);
  }
  linhas.push('', 'Qualquer dúvida, estamos à disposição!', 'Equipe Fisicore 💙');
  return linhas.join('\n');
}

/** Mesma ideia, para o documento CONSOLIDADO (Group) -- `quantidade`/
 * `valorFormatado` DEVEM vir de campos CANONICOS do backend (`grupo.
 * quantidade_cobrancas_grupo`/`grupo.valor_total_grupo`), NUNCA da
 * soma/contagem dos componentes visiveis so' nesta pagina (ver
 * `resumoConfirmacaoDocumentoGrupo`/investigacao E.1, "AJUSTE 2") --
 * responsabilidade do CHAMADOR fornecer o valor certo, esta funcao so'
 * formata. Nunca lista as inscricoes individuais nesta fase (Secao 10
 * da instrucao). */
export function construirMensagemWhatsAppConsolidado({
  alunoNome, valorFormatado, vencimentoFormatado, quantidade, urlBoleto, linhaDigitavel,
}) {
  const urlValida = normalizarUrlBoleto(urlBoleto);
  const linhas = [
    'Olá! 😊',
    '',
    `Segue a cobrança referente à participação de ${alunoNome || 'aluno(a)'} nos eventos da Fisicore.`,
    '',
    `Valor: ${valorFormatado}`,
    `Vencimento: ${vencimentoFormatado}`,
    '',
    `Este boleto reúne ${quantidade ?? '—'} cobranças.`,
  ];
  if (urlValida) {
    linhas.push('', 'Acesse o boleto:', urlValida);
  }
  if (linhaDigitavel) {
    linhas.push('', 'Linha digitável:', linhaDigitavel);
  }
  linhas.push('', 'Qualquer dúvida, estamos à disposição!', 'Equipe Fisicore 💙');
  return linhas.join('\n');
}

/** Autorizacao (client-side, nunca financeira) do botao WhatsApp
 * INDIVIDUAL: exige documento EMITIDO (mesmo campo `status_emissao` ja
 * usado para decidir outras acoes desta tabela -- nunca reconstruido a
 * partir de outros campos) + telefone valido + pelo menos uma forma
 * util de pagamento (URL ou linha digitavel). `telefoneNormalizado' ja
 * deve vir normalizado (ver `normalizarTelefoneWhatsApp`). */
export function parcelaElegivelParaWhatsApp(parcela, telefoneNormalizado) {
  if (!parcela || parcela.status_emissao !== 'EMITIDO') return false;
  if (!telefoneNormalizado) return false;
  return Boolean(normalizarUrlBoleto(parcela.url_boleto) || parcela.linha_digitavel);
}

/** Mesma ideia para o documento CONSOLIDADO. Auditoria final E.1 (Secao
 * 4, CRITICA): NAO usa mais `grupoTemAcao(grupo, 'CANCELAR')` como
 * proxy de "documento vigente" -- WhatsApp e cancelamento sao conceitos
 * diferentes. Um grupo EMITIDO/REEMITIDO cuja ultima tentativa de
 * cancelamento falhou com ERRO_PROVIDER perde a acao CANCELAR (bloqueio
 * permanente de retry, decisao deliberada em `derivar_estado_e_acoes_
 * grupo`) mas o documento continua vigente e compartilhavel -- esconder
 * o WhatsApp nesse caso era um bug (B != C, documento compartilhavel
 * != documento cancelavel).
 *
 * Criterio: existencia de um identificador do documento VIGENTE
 * (`url_boleto_grupo`/`linha_digitavel_grupo`, ja' escopados pelo
 * backend ao `attempt_vigente_id`) E `grupoTemAcao(grupo,
 * 'VERIFICAR_PAGAMENTO')` -- essa acao e' anunciada pelo backend
 * SEMPRE (e SOMENTE) que o grupo esta' EMITIDO/REEMITIDO com Attempt
 * vigente ESTAVEL (`derivar_estado_e_acoes_grupo`, `acoes =
 * ["VERIFICAR_PAGAMENTO"]` incondicional nesse branch, independente do
 * resultado de qualquer CancellationAttempt) -- nunca presente durante
 * CANCELANDO/CANCELAMENTO_INCERTO/CONFLITO_PAGAMENTO, onde o documento
 * TAMBEM tem identificador vigente carregado mas compartilhar seria
 * inadequado (cancelamento em andamento/resultado incerto/disputa de
 * pagamento exigindo revisao manual -- documento pode deixar de valer
 * a qualquer momento). Reusa uma autorizacao JA calculada pelo backend
 * (mesmo padrao de `grupoTemAcao`), nunca reconstroi logica de estado
 * nem compara `estado_documento_grupo` como string aqui. */
export function grupoTemDocumentoCompartilhavel(grupo) {
  if (!grupoTemAcao(grupo, 'VERIFICAR_PAGAMENTO')) return false;
  return Boolean(normalizarUrlBoleto(grupo?.url_boleto_grupo) || grupo?.linha_digitavel_grupo);
}

export function grupoElegivelParaWhatsApp(grupo, telefoneNormalizado) {
  if (!telefoneNormalizado) return false;
  return grupoTemDocumentoCompartilhavel(grupo);
}
